import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import {
  MIN_QUERY_LENGTH,
  pushRecentCluster,
  type SearchCluster,
  type SearchResult,
  type SearchScope,
} from './searchTypes';

// The search data hook. Owns the query, the scope, the debounced /api/search
// fetch, and the in-memory Recent list (seeded from /api/search/recent on mount;
// cross-session persistence is out of scope for this story). Returns everything
// the panel renders plus addRecent (called when a result is opened).

const DEBOUNCE_MS = 220;
const RECENT_CAP = 10;
const EMPTY: SearchResult = { clusters: [], matchCount: 0, capped: false };

// Recent persists across sessions in localStorage (Freddi). Wrapped so a
// private-mode / quota / SSR failure is non-fatal — Recent just falls back to
// the server seed.
const RECENT_KEY = 'bbbk:search:recent';
function loadRecent(): SearchCluster[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
function saveRecent(list: SearchCluster[]) {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(list));
  } catch {
    /* non-fatal */
  }
}

export function useSearch() {
  const [q, setQ] = useState('');
  const [scope, setScope] = useState<SearchScope>('all');
  const [result, setResult] = useState<SearchResult | null>(null);
  const [loading, setLoading] = useState(false);
  // Recent is ONLY the user's own opened records (the 10 most recent), hydrated
  // from localStorage and persisted on every open — no server seed. A brand-new
  // user starts empty (the panel shows the empty state).
  const [recent, setRecent] = useState<SearchCluster[]>(loadRecent);
  // Guards against an out-of-order response overwriting a newer one.
  const seq = useRef(0);

  // Debounced search on q/scope. Below the minimum length we don't fetch — the
  // panel shows Recent (empty term) or a "keep typing" hint (1–2 chars). This is
  // the client half of the broad-query safeguard; the server enforces it too.
  useEffect(() => {
    const term = q.trim();
    // Drop the previous result immediately so stale rows for the OLD query can
    // never be shown/opened while the new one loads (keyboard + click use `q`).
    setResult(null);
    if (term.length < MIN_QUERY_LENGTH) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const mine = ++seq.current;
    const t = setTimeout(() => {
      api<SearchResult>(`/search?q=${encodeURIComponent(term)}&scope=${scope}`)
        .then((r) => {
          if (mine === seq.current) setResult(r.data);
        })
        .catch(() => {
          if (mine === seq.current) setResult(EMPTY);
        })
        .finally(() => {
          if (mine === seq.current) setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q, scope]);

  const addRecent = useCallback((cluster: SearchCluster) => {
    // Bookings + leave head no cluster — they're context only, never Recent items.
    if (cluster.header.type === 'booking' || cluster.header.type === 'leave') return;
    setRecent((prev) => {
      const next = pushRecentCluster(prev, cluster, RECENT_CAP);
      saveRecent(next); // persist across sessions
      return next;
    });
  }, []);

  return { q, setQ, scope, setScope, result, loading, recent, addRecent };
}
